/* 校外人士進館統計 */
import { useRef, useState, useLayoutEffect, useMemo, useEffect } from "react";
import PositionManagement from '../../inventory_check/views/position_management'
import { useTranslation } from 'react-i18next';
import { GalleryMain } from '../../media_classification_storage/views/document_management'
import { Row, Col, Modal, Button, Form, Typography, Seperator, DatePicker, Tag, TimeSelector, Card, useCRUDController, message } from "../../ui/ot"
import { StaffForm } from '../../organization_structure/Staff';
import dayjs from 'dayjs';
import axios from "axios";
import { QRCode } from 'antd';
import MediaContent from '../../media_classification_storage/views/media_content';
import Upload_modal from '../../media_classification_storage/Upload_ot_modal';
import styled, { css, keyframes } from 'styled-components';
import { Users, UserCheck, AlertTriangle, Clock, QrCode } from "lucide-react"

/* 工具與變數 */
const colorVar = (name, fallback) => `var(${name}, ${fallback})`;


/* 版面容器 */
const Main = styled.main`
  margin-left: auto; /* mx-auto */
  margin-right: auto;
  padding-left: 1rem; /* px-4 */
  padding-right: 1rem;
  padding-top: 2rem; /* py-8 */
  padding-bottom: 2rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr; /* mobile 單欄 */
  gap: 2rem; /* gap-8 */

  @media (min-width: 768px) {
    /* md 以上改為 1:2 */
    grid-template-columns: 1fr 2fr;
  }

  @media (min-width: 1280px) {
    /* xl 繼續使用 1:2 比例（4/12 vs 8/12） */
    grid-template-columns: repeat(12, minmax(0, 1fr));
  }
`;

const Aside = styled.aside`
  @media (min-width: 768px) {
    grid-column: span 1 / span 1; /* md: 佔 1fr (左邊) */
  }
  @media (min-width: 1280px) {
    grid-column: span 4 / span 4; /* xl: 左半 (4/12) */
  }
`;

const Section = styled.section`
  @media (min-width: 768px) {
    grid-column: span 1 / span 1; /* md: 佔 2fr (右邊) */
  }
  @media (min-width: 1280px) {
    grid-column: span 8 / span 8; /* xl: 右半 (8/12) */
  }
  & > * + * { margin-top: 2rem; } /* space-y-8 */
`;


/* 卡片 */
const CardStyle = styled.div`
  background: ${colorVar("--card", "#ffffff")};
  border: 0;
  border-radius: 1rem; /* rounded-2xl */
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0,0,0,.08); /* shadow-lg/xl */
  transition: box-shadow .3s ease, transform .3s ease;

  &:hover {
    box-shadow: 0 12px 32px rgba(0,0,0,.12);
  }
`;

const CardHeader = styled.header`
  padding: 16px; /* p-4 默認，左側卡片用 p-6 另設 */
  background: ${colorVar("--primary", "#0ea5e9")};
  color: ${colorVar("--primary-foreground", "#ffffff")};

  ${props => props.$size === "lg" && css`
    padding: 24px; /* p-6 */
  `}
${props => props.$variant === "purple" && css`
    background: #6b21a8; /* bg-blue-600 */
    color: #ffffff;
  `}
  ${props => props.$variant === "blue" && css`
    background: #2563eb; /* bg-blue-600 */
    color: #ffffff;
  `}
  ${props => props.$variant === "green" && css`
    background: #16a34a; /* bg-green-600 */
    color: #ffffff;
  `}
  ${props => props.$variant === "red" && css`
    background: #dc2626; /* bg-red-600 */
    color: #ffffff;
  `}
`;

const CardTitleStyle = styled.h3`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px; /* gap-3 */
  font-weight: 700;
  line-height: 1.3;
  ${props => props.$size === "xl" ? css`font-size: 1.25rem;` : css`font-size: 1.125rem;`} /* text-xl / text-lg */
`;

const CardContent = styled.div`
  padding: 0.5rem; /* p-8 */
  & > * + * { margin-top: 2rem; } /* space-y-8 */
`;

/* 公用元素 */
const CenterStack = styled.div`
  text-align: center;
  & > * + * { margin-top: 1rem; } /* space-y-4 */
`;

const NumberStack = styled.div`
  & > * + * { margin-top: .5rem; } /* space-y-2 */
`;

const MutedRow = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 8px;
  color: ${colorVar("--muted-foreground", "#6b7280")};
  font-weight: 600;
`;

/* 二欄狀態卡片區 */
const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr; /* md:1 → 2 */
  gap: 1.5rem; /* gap-6 */

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;
const Chip = styled.div`
  ${'' /* display: inline-flex;  */}
  align-items: center; gap: 8px;
  padding: 8px 16px;
  border-radius: 9999px;
  font-weight: 600;
  font-size: .95rem;
  background: ${props => props.$tone?.chipBg};
  color: ${props => props.$tone?.chipText};

`;
const ChipS = styled.div`
  display: grid;
  place-items: center;
  width: ${props => props.$size ?? 120}px;
  aspect-ratio: 1 / 1;
  border-radius: ${props => props.$radius ?? 12}px;
  font-weight: 600;
  font-size: 1.5rem;
  background: ${props => props.$tone?.chipBg};
  color: ${props => props.$tone?.chipText};
  white-space: nowrap;
`;

/* 顏色系統工具（背景與字色） */
const tone = {
    blue: {
        bgSoft: "rgba(239, 246, 255, .5)", // blue-50/50
        text: "#1d4ed8", // blue-600
        chipBg: "#dbeafe", // blue-100
        chipText: "#1d4ed8",
        dot: "#3b82f6", // blue-500
    },
    green: {
        bgSoft: "rgba(240, 253, 244, .5)",
        text: "#15803d",
        chipBg: "#dcfce7",
        chipText: "#166534",
        dot: "#22c55e",
    },
    red: {
        bgSoft: "rgba(254, 242, 242, .5)",
        text: "#b91c1c",
        chipBg: "#fee2e2",
        chipText: "#b91c1c",
        dot: "#ef4444",
    },
    purple: {
        bgSoft: "rgba(250, 245, 255, .5)", // purple-50/50
        text: "#6b21a8", // purple-700
        chipBg: "#f3e8ff", // purple-100
        chipText: "#6b21a8",
        dot: "#a855f7", // purple-500
    }
};

const SoftCard = styled(CardStyle)`
  ${props => props.$tone && css`background: ${props.$tone.bgSoft};`}
`;

/* QR 區塊（提供 fallback + 固定種子） */
const QrOuter = styled.div`
  display: flex; justify-content: center;
`;
const Note = styled.p`
  margin: 0; font-size: .875rem; line-height: 1.6; color: ${colorVar("--muted-foreground", "#6b7280")};
`;


const DEFAULT_SPEED = 40; // 每秒滾動 px

// 用 CSS 變數承載距離，避免 keyframes 需要動態生成
const scrollUp = keyframes`
  from { transform: translateY(0); }
  to   { transform: translateY(calc(var(--distance, 1px) * -1)); }
`;

const MarqueeViewport = styled.div`
  position: relative;
  overflow: hidden;
  height: ${p => p.$height || "280px"};

  /* 上下淡化，可移除 */
  -webkit-mask-image: linear-gradient(to bottom, transparent, black 10%, black 90%, transparent);
  mask-image: linear-gradient(to bottom, transparent, black 10%, black 90%, transparent);
`;

const MarqueeTrack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.$gap || "0.5rem"};
  /* 供 keyframes 使用的距離變數（單份內容高度） */
  --distance: ${p => (p.$distance || 1) + "px"};

  ${p =>
        p.$animate &&
        css`
      animation: ${scrollUp} ${Math.max(1, p.$duration || 1)}s linear infinite;

      ${MarqueeViewport}:hover & {
        animation-play-state: paused;
      }
      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
`;

const Chunk = styled.div`
  display: flex;
  flex-direction: column;
`;

function VerticalMarquee({
    height = "280px",
    gap = "0.5rem",
    speedPxPerSec = DEFAULT_SPEED,
    renderContent,
    disableWhenTooShort = false,
}) {
    const viewportRef = useRef(null);
    const chunkRef = useRef(null);

    const [viewportH, setViewportH] = useState(0);
    const [contentH, setContentH] = useState(0);

    useLayoutEffect(() => {
        const measure = () => {
            if (!viewportRef.current || !chunkRef.current) return;
            setViewportH(viewportRef.current.clientHeight);
            setContentH(chunkRef.current.clientHeight);
        };
        measure();

        let ro;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(measure);
            if (viewportRef.current) ro.observe(viewportRef.current);
            if (chunkRef.current) ro.observe(chunkRef.current);
        } else {
            window.addEventListener("resize", measure);
        }
        window.addEventListener("load", measure);

        return () => {
            if (ro) ro.disconnect();
            window.removeEventListener("resize", measure);
            window.removeEventListener("load", measure);
        };
    }, []);

    // 需要幾份來鋪滿？至少 2 份做無縫；不足時補到 >= 2×viewport
    const repeatCount = useMemo(() => {
        if (!contentH || !viewportH) return 2;
        if (contentH >= viewportH) return 2;
        return Math.max(2, Math.ceil((viewportH * 2) / contentH));
    }, [contentH, viewportH]);

    const shouldDisableAnimation =
        disableWhenTooShort && contentH > 0 && contentH < viewportH;

    // 動畫距離（單份高度），時間 = 距離 / 速度
    const distance = contentH || 1;
    const durationSec = Math.max(1, distance / (speedPxPerSec || DEFAULT_SPEED));

    return (
        <MarqueeViewport ref={viewportRef} $height={height}>
            <MarqueeTrack
                $gap={gap}
                $duration={durationSec}
                $distance={distance}
                $animate={!shouldDisableAnimation}
            >
                {/* 第一份：量測用 */}
                <Chunk ref={chunkRef}>{renderContent?.()}</Chunk>

                {/* 依需求複製（內容不足時自動補份數） */}
                {!shouldDisableAnimation &&
                    Array.from({ length: repeatCount - 1 }).map((_, i) => (
                        <Chunk key={`dup-${i}`}>{renderContent?.()}</Chunk>
                    ))}
            </MarqueeTrack>
        </MarqueeViewport>
    );
}


function useSleepWakeDetector(onWake) {
    useEffect(() => {
        let lastTime = Date.now();
        const interval = setInterval(() => {
            const now = Date.now();
            const diff = now - lastTime;
            console.log(diff)
            // 判斷時間跳躍，例如間隔 > 3 秒
            if (diff > 20000) {
                onWake?.(diff);
            }

            lastTime = now;
        }, 1000);

        return () => clearInterval(interval);
    }, [onWake]);
}


const AreaManagement = ({
    propsForm,
    onFormFinish = () => { },
    defaultSelectedRowsInfo = [],
    defaultSelectedRowsKeys = [],
    keyPrefix = "reservation_registration_and_space_management.space_management",
    propsModeActiveTabHidden = true,
    depth = null,
    only_depth = null,
    onSelectChange = (() => { }),
    toolBarRender = (tools) => ([...tools]),
    rowSelectionMode = 'checkbox',
    readonly = true,
    extraParams = {},
    propsClassifyStructureTypeId = null,
    get_position_mode = null,
    admin = false,
    layoutMode = "21",
    // layoutMode = "41",
    propsNotAreaIdArr = [2],
    // propsNotAreaIdArr,
    propsLoading,
    propsSetLoading,
    ...props
}) => {
    const { t } = useTranslation('translation', { keyPrefix })
    const [staff_data, set_staff_data] = useState({})
    const [loading_, setLoading_] = useState(false)
    const [loading, setLoading] = [propsLoading ?? loading_, propsSetLoading ?? setLoading_]
    const [dayPickerSearch, setDayPickerSearch] = useState()
    const actionRef = useRef();
    const actionRef1 = useRef();
    const actionRef2 = useRef();
    const actionRef3 = useRef();
    const actionRef4 = useRef();
    const actionRefLeft = useRef();
    const actionRefRight = useRef();
    const [nowTime, setNowTime] = useState(dayjs().format('YYYY-MM-DD HH:mm:ss'))
    const [form_] = Form.useForm();
    const form = propsForm || form_
    const [date_now] = useState(Date.now())

    // 錯誤日誌 Modal 狀態
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorLogs, setErrorLogs] = useState([]);
    const [loadingErrorLogs, setLoadingErrorLogs] = useState(false);

    const fetchErrorLogs = async () => {
        setLoadingErrorLogs(true);
        try {
            const resp = await axios.get('/queue_management/backend/errorLog');
            const data = resp?.data;
            // API 可能回傳 status 為 'sucess'（錯字）或 'success'
            if (data && (data.status === 'sucess' || data.status === 'success')) {
                setErrorLogs(Array.isArray(data.data) ? data.data : []);
            } else {
                setErrorLogs([]);
                message?.error?.('無法取得錯誤日誌');
            }
        } catch (e) {
            console.error(e);
            message?.error?.('取得錯誤日誌失敗');
        } finally {
            setLoadingErrorLogs(false);
        }
    };

    // 全域監聽 Enter 鍵以顯示 Modal（按下即開啟並抓取日誌）
    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'Enter') {
                setShowErrorModal(true);
                fetchErrorLogs();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    
    useSleepWakeDetector((diff) => {
        window.location.reload();
    });
    
    // 第 4 個參數：resolveTone(item) => tone 物件
    const renderList = (
        list = [],
        [onPrimaryAction, primaryLabel, onSecondaryAction, secondaryLabel] = [],
        className = "space-y-4 text-xl text-gray-700",
        resolveTone = () => tone.red // 預設紅色
    ) => (
        <ul style={{ padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "12px" }}>
            {[...list].map((item) => {
                // 允許傳函式或直接傳 tone 物件
                const t = (typeof resolveTone === "function" ? resolveTone(item) : resolveTone) || tone.purple;

                return (
                    <li
                        key={item?.number}
                        className={className}
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            fontSize: "3.5vmin",
                        }}
                    >
                        <div
                            className="w-60 flex items-center justify-between bg-white rounded-xl px-6 py-4 shadow-sm hover:shadow-md transition-shadow"
                            style={{ border: `1px solid ${t.chipBg}` }} // 動態邊框色
                        >
                            <div className="flex items-center gap-4">
                                {/* 左側號碼色塊 */}
                                <span
                                    className="font-bold px-3 py-2 rounded-lg text-lg"
                                    style={{ color: t.chipText, backgroundColor: t.chipBg }}
                                >
                                    {item?.number}
                                </span>

                                {/* 名稱文字色 */}
                                <span className="font-medium text-lg" style={{ color: t.text }}>
                                    {item?.name}
                                </span>
                            </div>

                            {/* 右側小圓點色 */}
                            <div className="w-3 h-3 rounded-full animate-pulse" style={{ backgroundColor: t.dot }} />
                        </div>

                        <div>
                            {admin && onPrimaryAction && (
                                <Button
                                    size="small"
                                    style={{
                                        backgroundColor: "#FFDDC1",
                                        marginRight: "10px",
                                        color: "#5A5245",
                                    }}
                                    onClick={() => onPrimaryAction(item)}
                                >
                                    {primaryLabel}
                                </Button>
                            )}
                            {admin && onSecondaryAction && (
                                <Button
                                    size="small"
                                    style={{ backgroundColor: "#FFE6E6", color: "#5A3335" }}
                                    onClick={() => onSecondaryAction(item)}
                                >
                                    {secondaryLabel}
                                </Button>
                            )}
                        </div>
                    </li>
                );
            })}
        </ul>
    );


    const staffTaskAreaController = useCRUDController({
        apiRoute: '/queue_management/backend/staff_task_area',
        onFinish: ({ axiosMode, status }) => {
            if (status === "success") {
                if (axiosMode === "patch") {
                    setLoading(false)
                }
            }
        },
        onBefore: () => {
            setLoading(true)
        }
    })
    const maskString = (input) => {
        if (!input || input.length <= 1) return input;
        const firstChar = input[0];
        const lastChar = input.length > 2 ? input[input.length - 1] : '';
        const middle = 'Ｏ';
        return firstChar + middle + lastChar;
    };
    const graduatePersonController = useCRUDController({
        apiRoute: '/nknu/graduate_person',
        onFinish: ({ axiosMode, status, values }) => {
            values?.forEach(item => {
                if (item?.is_insert === 1) {
                    message.success(<div style={{ fontSize: '4vmin' }}>{maskString(item?.property_json?.data?.[0]?.cName) + ' 先生／小姐，請再靠卡進入'}</div>)
                } else {
                    message.warning(<div style={{ fontSize: '4vmin' }}>{maskString(item?.property_json?.data?.[0]?.cName) + ' 先生／小姐，已偵測到您靠卡，卡機同步中，請稍後'}</div>)
                }
            });
        },
        onBefore: () => {
        }
    })
    useEffect(() => {
        const refreshInterval = setInterval(() => {
            if (!admin) {
                actionRef.current?.reloadAndRest();
                actionRefLeft.current?.reloadAndRest();
                actionRefRight.current?.reloadAndRest();
                actionRef1.current?.reloadAndRest();
                actionRef2.current?.reloadAndRest();
                actionRef3.current?.reloadAndRest();
                actionRef4.current?.reloadAndRest();
                graduatePersonController.getData({
                    period_last_edit_time: dayjs().format('YYYY-MM-DD HH:mm:ss')
                });
            }
        }, 20000)
        const nowTimeInterval = setInterval(() => {
            setNowTime(dayjs().format('YYYY-MM-DD HH:mm:ss'))
        }, 1000)
        return () => {
            clearInterval(refreshInterval)
            clearInterval(nowTimeInterval)
        }
    }, [])
    const settingProps = {
        actionRef: actionRef,
        rowSelectionMode: '',
        readonly: readonly,
        propsForm: form,
        onSelectChange: onSelectChange,
        depth: depth,
        only_depth: only_depth,
        defaultSelectedRowsInfo: defaultSelectedRowsInfo,
        defaultSelectedRowsKeys: defaultSelectedRowsKeys,
        initialValues: {
            staff_name: staff_data?.staff_name,
            create_time: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        },
        api: '/queue_management/backend/area',
        propsActiveTabHidden: true,
        propsActiveKey: '',
        propsModeActiveTabHidden: propsModeActiveTabHidden,
        propsModeActiveKey: 'list',
        get_position_mode: get_position_mode,
        extraParams: {
            date: dayPickerSearch ? new Intl.DateTimeFormat('en-US').format(dayPickerSearch) : null,
            classify_structure_id: 55,
            classify_structure_type_id: null,
            start_time: dayjs().format('YYYY-MM-DD'),
            not_area_id_arr: propsNotAreaIdArr,
            ...extraParams
        },
        keyPrefix: `${keyPrefix}.position`,
        toolBarRender: (tools) => toolBarRender([
            ...(readonly ? [
                <>
                    {/* 
                <Row align="middle" gutter={[8, 8]}>
                    <Col>
                        <Typography.Title level={2}>
                            {nowTime}
                        </Typography.Title>
                    </Col>
                </Row> */}
                </>
            ] : [<Row align="middle" gutter={[8, 8]}>
                <Col>
                    <Typography.Title level={4}>
                        {t('reserve_time')}
                    </Typography.Title>
                </Col>
                <Col>
                    <DatePicker.Time
                        value={dayPickerSearch ? dayPickerSearch : null}
                        granularity="day"
                        format={'YYYY-MM-DD'}
                        onValueChange={(value) => {
                            let custom_value = '';
                            custom_value = value?.target ? value?.target.value : value
                            if (value) setDayPickerSearch(new Date(custom_value))
                            else setDayPickerSearch()
                        }}
                        onChange={(value) => {
                            let custom_value = '';
                            custom_value = value?.target ? value?.target.value : value
                            if (value) setDayPickerSearch(new Date(custom_value))
                            else setDayPickerSearch()
                        }}
                    />
                </Col>
            </Row>]),
            ...tools,
        ]),
        customFormItems: (fieldName, formValues, onValuesChange = () => { }, propsCustomForm) => {
            const staff_id = formValues?.staff_id
            const initialSelectedTimes = formValues?.position_can_reserve_time ?? [];
            const selectableRanges = ["00:00-24:00"]
            const classify_structure_type_id = propsCustomForm.getFieldValue('position_classify_structure_type_data.0.classify_structure_type_id')
            const position_classify_structure_type_data = propsCustomForm.getFieldValue('position_classify_structure_type_data')
            return (<>
                <Row>
                    <Col span={24}>
                        <Seperator />
                    </Col>
                    <Col span={24}>
                        <Typography.Title level={4}>
                            {t('seating_and_space_can_reserve_time')}
                        </Typography.Title>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="position_can_reserve_time"
                            label={''}
                        >
                            <TimeSelector
                                slotInterval={60} // 設定為 60 分鐘時間槽
                                initialSelectedTimes={initialSelectedTimes}
                                initialDisabledTimes={[]}
                                selectableRanges={selectableRanges}  // 可選範圍
                                maxHours={null} // 最大選擇上限設為 4 小時
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Seperator />
                    </Col>
                    <Col span={24}>
                        <Typography.Title level={4}>
                            {t('seating_and_space_image')}
                        </Typography.Title>
                    </Col>
                    <Col span={24} key={String(position_classify_structure_type_data)}>
                        <Form.Item
                            labelCol={4}
                            label={t('folder')}
                            name={'position_classify_structure_type_data.0.classify_structure_type_id'}
                        >
                            <MediaContentForm propsDeleteModal={true} />
                        </Form.Item>
                    </Col>
                    {classify_structure_type_id && <Col span={24}>
                        <Form.Item
                            labelCol={4}
                            label={t('photo')}
                            name={'position_classify_structure_type_data.0.classify_structure_type_file'}
                        >
                            <Upload_modal
                                classify_structure_type_id={classify_structure_type_id}
                                propsMode='edit'
                            />
                        </Form.Item>
                    </Col>}
                    <Col span={24}>
                        <Seperator />
                    </Col>
                    <Col span={24}>
                        <Typography.Title level={4}>
                            {t('create_user')}
                        </Typography.Title>
                    </Col>
                    <Col span={24}>
                        <StaffSelfForm
                            staff_id={staff_id}
                            propsForm={propsCustomForm}
                        />
                    </Col>
                </Row>
            </>)
        },
        customColumns:
            [

                {
                    title: t('file'),
                    key: "classify_structure_type_id",
                    dataIndex: "position_classify_structure_type_data",
                    render: (text, record, index) => {
                        let position_classify_structure_type_datas = record?.position_classify_structure_type_data || []
                        return <>
                            {
                                position_classify_structure_type_datas.map((position_classify_structure_type_data) => {
                                    return (
                                        <Button onClick={() => {
                                            Modal.info({
                                                title: '',
                                                content: <>
                                                    <GalleryMain
                                                        wrap={true}
                                                        propsClassifyStructureTypeId={position_classify_structure_type_data?.classify_structure_type_id}
                                                    />
                                                </>
                                            })
                                        }}>檢視圖片</Button>
                                    )
                                })
                            }

                        </>
                    }
                },
                {
                    title: t("seating_and_space_status"),
                    key: "seating_and_space_status",
                    dataIndex: "seating_and_space_status",
                    canFilter: true,
                    filterType: 'search',
                    width: "10rem",
                },
                {
                    title: t("can_reserve_time_string"),
                    key: "can_reserve_time_string",
                    dataIndex: "can_reserve_time_string",
                    canFilter: true,
                    filterType: 'search',
                    width: "10rem",
                    render: (text, record) => <>
                        <Row>
                            {record?.position_can_reserve_time?.map(record_time => <>
                                <Col span={12}>
                                    <Tag color="geekblue">
                                        {record_time}
                                    </Tag>
                                </Col>
                            </>)}
                        </Row>
                    </>
                },
                {
                    title: t("create_user"),
                    key: "staff_name",
                    dataIndex: "staff_name",
                    canFilter: true,
                    filterType: 'search',
                },
                {
                    title: t("create_time"),
                    key: "create_time",
                    dataIndex: "create_time",
                    canFilter: true,
                    filterType: 'search',
                    render: (text, record, index) => <>
                        <Row>
                            <Col span={24}>
                                {record?.create_time}
                            </Col>
                        </Row>
                    </>
                },
            ],
        propsShowKeys:
            [
                { key: "name", name: "name", hidden: false, require: false },
                { key: "name_en", name: "name_en", hidden: false, require: false },
                { key: "location", name: "location", hidden: true, require: false },
                { key: "space", name: "space", hidden: true, require: false },
                { key: "capacity", name: "capacity", hidden: true, require: false },
                { key: "row", name: "row", hidden: true, require: false },
                { key: "column", name: "column", hidden: true, require: false },
                { key: "height", name: "height", hidden: true, require: false },
                { key: "position_attribute_data", name: "position_attribute_data", hidden: true, require: false },
                { key: "Seperator1", name: "Seperator1", hidden: true, require: false },
                { key: "Seperator2", name: "Seperator2", hidden: true, require: false },
                { key: "x", name: "x", hidden: true, require: false },
                { key: "y", name: "y", hidden: true, require: false },
                { key: "z", name: "z", hidden: true, require: false },
                { key: "max", name: "max", hidden: true, require: false },
                { key: "min", name: "min", hidden: true, require: false },
                { key: "row", name: "row", hidden: true, require: false },
                { key: "column", name: "column", hidden: true, require: false },
                { key: "height", name: "height", hidden: true, require: false },
            ]
        ,
        propsClassifyStructureTypeId: null,
        cardRender: (record, { PatchButton, DeleteButton }) => {
            let list =
                record?.queue_area_data?.map(item =>
                ({
                    ...item,
                    ...(item?.queue_task_data?.find(_ => true)),
                    queue_task_data: item?.queue_task_data?.map(itemItem => (
                        {
                            ...itemItem,
                            queue_id: item?.queue_id,
                            queue_staff_data: item?.queue_staff_data ?? [],
                            queue_area_data: item
                        }
                    )),
                    number: item?.queue_staff_data?.find(_ => true)?.regexp_replace_serial_name, name: item?.queue_staff_data?.find(_ => true)?.regexp_replace_name,
                })) ?? []
            if (record?.property_json?.order === "DESC") {
                list = list.reverse();
            }
            if (record?.property_json?.limit === 1) {
                list = list?.find(_ => true) !== undefined ? [list?.find(_ => true)] : [];
            }

            // --- shared: 一次定義，到處用 ---
            const colorMap = new Map([
                [1, tone.blue],
                [3, tone.red],
                [4, tone.green],
            ]);

            const getToneByAreaId = (id, fallback = tone.purple) =>
                colorMap.get(Number(id)) ?? fallback;

            // 提供給 renderList 用的 resolver（用同一組 colorMap）
            const resolveToneByArea = (item) => getToneByAreaId(item?.area_id);

            const toneColor = getToneByAreaId(record?.area_id);

            return <>
                <Col span={24} style={{
                    height: "100%",
                    flex: "0 0 auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    padding: "10px",
                    boxSizing: "border-box",
                }}>
                    {record?.property_json?.limit !== undefined && <>
                        {/* <Typography.Title level={1} color={record?.property_json?.title}>{record?.area_postion_data?.find(_ => true)?.name_language_data?.find(item => item?.language_culture_code === "zh-tw")?.language_culture_value}</Typography.Title> */}
                        <Chip $tone={tone.purple}>
                            <Typography.Title level={2} style={{ marginBottom: 0, paddingBottom: 0 }}>{record?.queue_area_data?.length}／{record?.max_limit}</Typography.Title>
                        </Chip>
                        <Typography.Title level={3}>{record?.property_json?.text}</Typography.Title>
                    </>}
                    {record?.property_json?.limit === undefined && <>
                        <div className="w-full h-full grid grid-cols-1 md:grid-cols-2 gap-3">
                            {/* 左半：Chip */}
                            <div className="w-full h-24 flex items-center justify-center">
                                <ChipS $tone={toneColor} className="block">
                                    共 {record?.queue_area_data?.length} 位
                                </ChipS>
                            </div>
                            <div className="w-full">
                                <VerticalMarquee
                                    height="clamp(300px, 40vh, 420px)"
                                    gap="0.75rem"
                                    speedPxPerSec={40}
                                    disableWhenTooShort={record?.queue_area_data?.length < 3} // 內容太少就不動畫，避免看見重複
                                    renderContent={() =>
                                        renderList(
                                            list,
                                            (record?.property_json?.area_data?.reduce((all, area) => {
                                                return [
                                                    ...all,
                                                    (item) => {
                                                        staffTaskAreaController.patchData([{
                                                            ...item,
                                                            queue_task_data: [...item?.queue_task_data?.map(itemItem => ({
                                                                ...itemItem,
                                                                queue_area_data: [{ ...area }]
                                                            }))]
                                                        }])
                                                    },
                                                    area?.area_name
                                                ]
                                            }, [])),
                                            record?.property_json?.content,
                                            resolveToneByArea
                                        )
                                    }
                                />
                            </div>
                        </div>

                    </>}
                </Col>
            </>
        },
        showSearch: false,
        reloadButton: false,
        manageColumnsButton: false,
        endMessage: <></>,
        cardModeSetting: {
            columnsCount: 1,
            singleRowHeight: '100vh',
        },
        layoutMode: layoutMode,
    }

    return <>
        {/* <NoCopyWrapperContainer> */}
        <div className="min-h-screen bg-background">
            <header className="bg-card border-b border-border sticky top-0 z-10 backdrop-blur-sm bg-card/95">
                <div className="mx-auto px-4 py-4">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="space-y-2">
                            <h1 className="text-4xl lg:text-5xl font-bold text-foreground tracking-tight items-center" style={{ marginBottom: 0 }}>高師大圖書館-校外人士進館統計</h1>
                        </div>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            <div className="flex items-center gap-2 bg-muted px-4 py-3 rounded-lg" style={{ fontSize: "1.5rem" }}>
                                <Clock className="h-5 w-5 text-muted-foreground" />
                                {nowTime}
                            </div>
                        </div>
                    </div>
                </div>
            </header>
            <Main>
                <Grid>
                    <Aside>
                        <CardStyle>
                            <CardHeader $variant="purple">
                                <CardTitleStyle $size="xl">
                                    <Users size={24} />
                                    已入館讀者統計
                                </CardTitleStyle>
                            </CardHeader>
                            <CardContent style={{ height: "70vh" }}>
                                <CenterStack>
                                    <NumberStack>
                                        <Col span={24}>
                                            {!loading && <PositionManagement
                                                {...settingProps}
                                                {...({
                                                    extraParams: {
                                                        ...settingProps?.extraParams,
                                                        not_area_id_arr: undefined,
                                                        area_id: 2
                                                    },
                                                    showMode: "card",
                                                    layoutMode: "children",
                                                    toolBarRender: (tools) => toolBarRender([]),
                                                })}
                                                actionRef={actionRefLeft}
                                                propsLoading={loading}
                                                propsSetLoading={setLoading}
                                            />}
                                        </Col>
                                    </NumberStack>
                                </CenterStack>

                                <div>
                                    <MutedRow>
                                        <QrCode size={20} />
                                        <span>入館 QR Code</span>
                                    </MutedRow>

                                    <QrOuter>
                                        {/* <QrFrame> */}
                                        <QRCode
                                            value={'https://queue-management-admin.nknu.edu.tw/login?date=' + date_now}
                                            size={256}
                                        />
                                        {/* </QrFrame> */}
                                    </QrOuter>

                                    <CenterStack>
                                        <Note>提供使用手機內建相機、Line應用程式掃描</Note>
                                        <Note>以便註冊或預約入館</Note>
                                    </CenterStack>
                                </div>
                            </CardContent>
                        </CardStyle>
                    </Aside>
                    <Section>
                        {/* 狀態卡片群組 */}
                        <TwoCol>
                            {/* 可入館 */}
                            <SoftCard $tone={tone.blue}>
                                <CardHeader $variant="blue">
                                    <CardTitleStyle>
                                        <UserCheck size={24} />
                                        可入館讀者
                                    </CardTitleStyle>
                                </CardHeader>
                                <CardContent>
                                    <CenterStack>
                                        {/* <Col span={!["21", "31"].includes(layoutMode) ? 24 : 18}> */}
                                        <Col span={24} style={{ height: "30vh" }}>

                                            {!loading && <PositionManagement
                                                {...settingProps}
                                                {...({
                                                    extraParams: {
                                                        ...settingProps?.extraParams,
                                                        not_area_id_arr: undefined,
                                                        area_id: 1

                                                    },
                                                    showMode: "card",
                                                    layoutMode: "children",
                                                    toolBarRender: (tools) => toolBarRender([]),
                                                })}
                                                actionRef={actionRef1}
                                                propsLoading={loading}
                                                propsSetLoading={setLoading}
                                            />}
                                        </Col>
                                    </CenterStack>
                                </CardContent>
                            </SoftCard>
                            {/* 待入館 */}
                            <SoftCard $tone={tone.green}>
                                <CardHeader $variant="green">
                                    <CardTitleStyle>
                                        <Users size={24} />
                                        待入館讀者
                                    </CardTitleStyle>
                                </CardHeader>
                                <CardContent>
                                    <CenterStack>
                                        {/* <Col span={!["21", "31"].includes(layoutMode) ? 24 : 18}> */}
                                        <Col span={24} style={{ height: "30vh" }}>
                                            {!loading && <PositionManagement
                                                {...settingProps}
                                                {...({
                                                    extraParams: {
                                                        ...settingProps?.extraParams,
                                                        not_area_id_arr: undefined,
                                                        area_id: 4

                                                    },
                                                    showMode: "card",
                                                    layoutMode: "children",
                                                    toolBarRender: (tools) => toolBarRender([]),
                                                })}
                                                actionRef={actionRef4}
                                                propsLoading={loading}
                                                propsSetLoading={setLoading}
                                            />}
                                        </Col>
                                    </CenterStack>
                                </CardContent>
                            </SoftCard>
                        </TwoCol>

                        {/* 逾時未進入者 */}
                        <SoftCard $tone={tone.red}>
                            <CardHeader $variant="red">
                                <CardTitleStyle $size="xl">
                                    <AlertTriangle size={24} />
                                    逾時未進入者
                                </CardTitleStyle>
                            </CardHeader>
                            <CardContent>
                                <CenterStack>
                                    {/* <Col span={!["21", "31"].includes(layoutMode) ? 24 : 18}> */}
                                    <Col span={24} style={{ height: "30vh" }}>
                                        {!loading && <PositionManagement
                                            {...settingProps}
                                            {...({
                                                extraParams: {
                                                    ...settingProps?.extraParams,
                                                    not_area_id_arr: undefined,
                                                    area_id: 3
                                                },
                                                showMode: "card",
                                                layoutMode: "children",
                                                toolBarRender: (tools) => toolBarRender([]),
                                            })}
                                            actionRef={actionRef3}
                                            propsLoading={loading}
                                            propsSetLoading={setLoading}
                                        />}
                                    </Col>
                                </CenterStack>
                            </CardContent>
                        </SoftCard>
                    </Section>
                </Grid>

                {/* 錯誤日誌 Modal（寬／高各為螢幕 50%） */}
                <Modal
                    open={showErrorModal}
                    footer={null}
                    onOk={() => fetchErrorLogs()}
                    okText={'重新整理'}
                    cancelText={'關閉'}
                    onCancel={() => setShowErrorModal(false)}
                    style={{ width: '50%' }}
                    bodyStyle={{ height: '50vh', overflowY: 'auto', padding: '16px' }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div style={{ fontSize: 18, fontWeight: 700 }}>系統錯誤日誌</div>
                        <div style={{ fontSize: 13, color: '#6b7280' }}>{loadingErrorLogs ? '讀取中...' : `${errorLogs.length} 筆`}</div>
                    </div>

                    <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13, lineHeight: 1.4 }}>
                        {errorLogs && errorLogs.length > 0 ? (
                            errorLogs.map((ln, idx) => (
                                <div key={idx} style={{ paddingBottom: 6, borderBottom: '1px solid rgba(0,0,0,0.04)', marginBottom: 6 }}>
                                    {ln}
                                </div>
                            ))
                        ) : (
                            <div style={{ color: '#6b7280' }}>目前沒有錯誤日誌</div>
                        )}
                    </div>
                </Modal>

            </Main>
        </div>
    </>
}

export default AreaManagement

const StaffSelfForm = ({
    propsForm,
    staff_id
}) => {
    const [form_] = Form.useForm()
    const form = propsForm ?? form_
    const [staffId, setStaffId] = useState()
    useEffect(() => {
        axios.get('/reservation_registration_and_space_management/staff')
            .then((response) => {
                let data = response.data
                setStaffId(data?.data?.find(_ => true)?.staff_id)
            });
    }, [])
    return <>
        <StaffForm
            mode='information'
            staff_id={staff_id ?? staffId}
        ></StaffForm>
    </>
}

export { StaffSelfForm }
const MediaContentForm = ({
    name = "",
    value,
    onChange = () => { },
    onValueChange = () => { },
    propsDeleteModal = false
}) => {
    return <>
        <MediaContent
            propsRowSelectionMode={'radio'}
            propsClassifyStructureTypeId={0}
            defaultSelectedRowsKeys={value !== "" ? [value] : []}
            defaultSelectedRowsInfo={value !== "" ? [{ classify_structure_type_id: value }] : []}
            propsOnValuesChange={(values) => {
                onChange({
                    name,
                    target: {
                        value: values?.find(_ => true)?.classify_structure_type_id
                    }
                })
                onValueChange({
                    name,
                    target: {
                        value: values?.find(_ => true)?.classify_structure_type_id
                    }
                })
            }}
            propsShowKeys={[
                { key: "font_color", name: "font_color", hidden: true, require: false },
                { key: "background_color", name: "background_color", hidden: true, require: false },
                { key: "hashtag_arr", name: "hashtag_arr", hidden: true, require: false },
                { key: "category_arr", name: "category_arr", hidden: true, require: false },
            ]}
            propsDeleteModal={propsDeleteModal}
        />
    </>
}

const CardNew = styled(Card)`
    background: ${(props) => props.bg || "#fff"};
    border: 2px solid ${(props) => props.border || "#ccc"};
    border-radius: 12px;
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
    padding: 16px;
`;
const CardTitle = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: ${(props) => props.color || "#333"};
`;

const NoCopyWrapper = styled.div`
  user-select: none; /* 禁止選中文字 */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
`;

const NoCopyWrapperContainer = ({
    children
}) => {
    useEffect(() => {
        // 禁止文字複製
        const disableCopy = (e) => e.preventDefault();

        // 禁止文字選取
        const disableSelect = (e) => e.preventDefault();

        // 禁止右鍵菜單
        const disableContextMenu = (e) => e.preventDefault();

        // 禁止多平台快捷鍵（Windows 和 macOS）
        const disableDevTools = (e) => {
            // 判斷常見的開發者工具快捷鍵
            if (
                e.key === "F12" || // F12
                (e.ctrlKey && e.shiftKey && e.key === "I") || // Ctrl + Shift + I (Windows)
                (e.ctrlKey && e.shiftKey && e.key === "J") || // Ctrl + Shift + J (Windows)
                (e.ctrlKey && e.key === "U") || // Ctrl + U (Windows)
                (e.metaKey && e.altKey && e.key === "I") || // Cmd + Option + I (macOS)
                (e.metaKey && e.key === "U") || // Cmd + U (macOS)
                (e.metaKey && e.altKey && e.key === "J") // Cmd + Option + J (macOS)
            ) {
                e.preventDefault();
                alert("此快捷鍵操作已被禁用！");
            }
        };

        // 添加事件監聽
        document.addEventListener("copy", disableCopy); // 禁止複製
        document.addEventListener("selectstart", disableSelect); // 禁止選取
        document.addEventListener("contextmenu", disableContextMenu); // 禁止右鍵
        document.addEventListener("keydown", disableDevTools); // 禁止快捷鍵
        // 检测 DevTools
        const checkDevTools = () => {
            const threshold = 160; // DevTools 打開後通常會導致 window 尺寸變化超過這個閾值
            if (window.outerWidth - window.innerWidth > threshold || window.outerHeight - window.innerHeight > threshold) {
                alert("偵測到開發者工具已開啟，網頁將自動關閉！");
                window.location.reload(); // 刷新頁面
            }
        };

        const interval = setInterval(checkDevTools, 1000);

        // 清除事件監聽
        return () => {
            document.removeEventListener("copy", disableCopy);
            document.removeEventListener("selectstart", disableSelect);
            document.removeEventListener("contextmenu", disableContextMenu);
            document.removeEventListener("keydown", disableDevTools);
            clearInterval(interval);
        };
    }, []);
    return (
        <NoCopyWrapper>
            {children}
        </NoCopyWrapper>
    );
};

export { NoCopyWrapperContainer }
